import { interfaces } from '@daas-consulting/core-cross-interfaces';
import * as crossUtils from '@daas-consulting/core-cross-utils';
import { formatDate } from './format-date';
import { getCriteria } from './get-criteria';
import { getGroupedModelKeys } from './get-grouped-model-keys';

interface GetFilterFromModelOptions<TFilter> {
  model: TFilter;
  labels: Map<string, string>;
  cases: Map<string, Map<string, string>>;
  isFixedMap?: Map<string, boolean>;
}

export function getFilterFromModel<TFilter>({
  model,
  labels,
  cases,
  isFixedMap,
}: GetFilterFromModelOptions<TFilter>): interfaces.Criteria[] {
  const rv: interfaces.Criteria[] = [];
  if (typeof model === 'object' && model !== null) {
    const groupedModelKeys = getGroupedModelKeys(model);
    groupedModelKeys.forEach((keys, property) => {
      if (keys.size === 1) {
        const criterias = getCriteriaWrapper<TFilter>({
          property: property,
          model,
          cases,
          labels,
        });
        criterias.forEach((x) => {
          rv.push(x);
        });
      } else {
        const auxFrom = getCriteriaWrapper<TFilter>({
          property: `${property}_from`,
          newProperty: `${property}From`,
          model,
          cases,
          labels,
        });
        const auxTo = getCriteriaWrapper<TFilter>({
          property: `${property}_to`,
          newProperty: `${property}To`,
          model,
          cases,
          labels,
        });
        if (auxFrom.length || auxTo.length) {
          const valuesSeparator =
            auxFrom.length && auxTo.length ? ' - ' : undefined;
          const rawValues = [
            crossUtils.criteria.getRawValueZeroOrUndefined(auxFrom),
            crossUtils.criteria.getRawValueZeroOrUndefined(auxTo),
          ].filter((item) => item);
          const value = [
            auxFrom.length ? auxFrom[0].value : undefined,
            valuesSeparator,
            auxTo.length ? auxTo[0].value : undefined,
          ]
            .filter((item) => item)
            .join('');
          const criteria: interfaces.Criteria = {
            key: property,
            label: labels.get(property) || property,
            value: fixValue({ cases, key: property, value }),
            attributes: [...auxFrom, ...auxTo],
            rawValue: rawValues,
            isFixed: isFixedMap?.get(property) ?? false,
          };
          rv.push(criteria);
        }
      }
    });
  }
  return rv;
}

interface FixValueOptions {
  cases: Map<string, Map<string, string>>;
  key: string;
  value: unknown;
}

interface GetCriteriaWrapperOptions<TFilter> {
  property: string;
  newProperty?: string;
  model: NonNullable<TFilter>;
  cases: Map<string, Map<string, string>>;
  labels: Map<string, string>;
}

export function getCriteriaWrapper<TFilter>({
  property,
  newProperty = property,
  model,
  cases,
  labels,
}: GetCriteriaWrapperOptions<TFilter>): interfaces.Criteria[] {
  const key = property as keyof TFilter;
  const value = model[key];
  const aux = getCriteria({
    value: value,
    key: newProperty,
    labels,
  });
  if (Array.isArray(aux) && aux.length > 0) {
    aux[0].rawValue = value;
    const fixedValue = fixValue({ cases, key: property, value });
    aux[0].value = formatDate(fixedValue);
  }

  return [...aux];
}

export function fixValue({ cases, key, value }: FixValueOptions): string {
  const rv = <string>(cases.get(key)?.get(`${value}`) || value);
  return rv;
}
